import { Bookmark, BookmarksFindResponse } from "./bookmarks-client";
import { BookmarkType } from "./bookmarks-config";
import {reduceBookmarkMetaby1, incrementBookmarkMetaby1} from "./bookmarks-state-nav";

const storage_key = "bookmarks";

export type LocalStorageBookmarks = {
  [key in BookmarkType]?: Array<string | number>;
}
export const getLocalBookmarks = (): LocalStorageBookmarks  => {
  const localBookmarks = localStorage.getItem(storage_key) || "{}";
  return JSON.parse(localBookmarks);
};

export const removeLocalBookmarks = () => {
  localStorage.removeItem(storage_key);
};
/**
 * Save bookmarks object to localstorage for logged out users
 */
export const setLocalBookmarks = (localBookmarks: any) => {
  localStorage.setItem(storage_key, JSON.stringify(localBookmarks));
};
/**
 * Toggle bookmark state locally for logged out users
 */
export const removeBookmarkLocally = (type: BookmarkType, id: string | number, eventData: Record<string, any>) => {
  const localBookmarks = getLocalBookmarks();
  localBookmarks[type] = localBookmarks[type]?.filter((value: any) => {
    return value !== id;
  });
  reduceBookmarkMetaby1(type);
  window.blueShiftEvent(type + "_bookmark_removed", eventData);
  window.gtmEvent("Bookmark", "Remove", type);
  setLocalBookmarks(localBookmarks);
};

/**
 * Toggle bookmark state locally for logged out users
 */
type AddBookmarkLocallyOptions = {
  closeSignupDialogCallback?: () => void;
};
export const addBookmarkLocally = (type: BookmarkType, id: string | number, eventData: Record<string, any>, options?: AddBookmarkLocallyOptions) => {
  const localBookmarks = getLocalBookmarks();
  if (!Boolean(localBookmarks[type])) {
    localBookmarks[type] = [];
  }

  if(!localBookmarks[type]?.some((bookmarkId) => bookmarkId == id)) {
    localBookmarks[type]?.push(id);
    incrementBookmarkMetaby1(type);
  }

  window.launchSignupLogin("signup", {
    allowConfirmationDialog: false,
    title: "We've bookmarked this " + getBookmarkTypeDisplayName(type),
    description: "Create an account or log in to view your bookmarks when you come back",
    closeCallback: options?.closeSignupDialogCallback
  });

  window.blueShiftEvent(type + "_bookmark_added", eventData);
  window.gtmEvent("Bookmark", "Add", type);
  setLocalBookmarks(localBookmarks);
};

/**
 * Get the display name for a bookmark type
 */
export const getBookmarkTypeDisplayName = (type: string) => {
  return {
    job: "Job",
    volunteering: "Volunteering",
    course: "Course",
    article: "Article",
    traveldeal: "Travel Deal"
  }[type];
};

/**
 * Move all local bookmarks to users account account to be used once logged in
 */
export const moveLocalBookmarksToAccount = async (bookmarkClientFindResults: BookmarksFindResponse['message']['results'], localBookmarkCallback: (type: BookmarkType, id: string | number) => Promise<void>) => {
  const localBookmarks = getLocalBookmarks();
  // Remove local bookmarks
  // TODO: Because of the asynchronous & event based nature of the bookmarks flow, we sometimes find ourselves on pages where
  // this function is called twice (sso-logged-in & search-results-success events) this can cause the local bookmarks
  // to be added to the user's account twice. Moving this line to the top of the function should prevent this from happening in most races
  removeLocalBookmarks(); 


  // No local bookmarks found, no need to proceed
  if (!localBookmarks || Object.keys(localBookmarks).length === 0) {
    return;
  }

  // Remove any bookmarks already in account from localBookmarks to avoid saving duplicates
  bookmarkClientFindResults.forEach((bookmark: Bookmark) => {
    const type = bookmark.type;
    const bookmarkId = bookmark[`${type}Id`];
    if (localBookmarks[type] && bookmarkId && localBookmarks[type].includes(bookmarkId)) {
      localBookmarks[type] = localBookmarks[type].filter((value: any) => {
        return value !== bookmarkId;
      });
    }
  });

  // Save all remaining local bookmarks to account
  const localBookmarkKeys = Object.keys(localBookmarks) as (keyof LocalStorageBookmarks)[];
  for (const type of localBookmarkKeys) {
    const bookmarks = localBookmarks[type]
    if (!bookmarks) {
      continue;
    }

    for (const id of bookmarks) {
      await localBookmarkCallback(type, id);
    }
  }
};
